import React, { useMemo, useState } from 'react';
import { Card, Table, Button, Space, DatePicker, Select, Typography, message, Tag } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import assetService, { type MovementReportRow } from '@/services/asset.service';

const { Text } = Typography;

const TYPE_COLOR: Record<string, string> = { issue: 'blue', return: 'green', transfer: 'gold' };

function toCsvEsc(v: string | null | undefined) {
  if (v == null || v === '') {
    return '';
  }
  const t = String(v);
  if (/[",\n\r]/.test(t)) {
    return `"${t.replace(/"/g, '""')}"`;
  }
  return t;
}

const AssetMovementReportPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(user?.is_super_admin || user?.permissions?.includes('asset.view'));
  const [dateFrom, setDateFrom] = useState<dayjs.Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<dayjs.Dayjs | null>(null);
  const [mType, setMType] = useState<string | undefined>(undefined);

  const { data: rows = [], isLoading, refetch } = useQuery({
    queryKey: ['assets', 'reports', 'movements', dateFrom?.format('YYYY-MM-DD'), dateTo?.format('YYYY-MM-DD'), mType],
    queryFn: () =>
      assetService.listMovementsReport({
        date_from: dateFrom ? dateFrom.format('YYYY-MM-DD') : undefined,
        date_to: dateTo ? dateTo.format('YYYY-MM-DD') : undefined,
        movement_type: mType,
      }),
    enabled: canView,
  });

  const cols: ColumnsType<MovementReportRow> = useMemo(
    () => [
      {
        title: 'When',
        dataIndex: 'created_at',
        key: 'c',
        width: 160,
        render: (d: string) => (d ? d.slice(0, 19).replace('T', ' ') : '—'),
      },
      {
        title: 'Type',
        dataIndex: 'movement_type',
        key: 't',
        width: 100,
        render: (s: string) => <Tag color={TYPE_COLOR[s] || 'default'}>{s}</Tag>,
      },
      { title: 'Asset code', dataIndex: 'asset_code', key: 'ac', width: 120 },
      { title: 'Asset', dataIndex: 'asset_name', key: 'an', ellipsis: true },
      { title: 'From (site / bldg / room)', key: 'f', ellipsis: true, render: (_: unknown, r) => [r.from_site, r.from_building, r.from_room].filter(Boolean).join(' / ') || '—' },
      { title: 'To (site / bldg / room)', key: 'to', ellipsis: true, render: (_: unknown, r) => [r.to_site, r.to_building, r.to_room].filter(Boolean).join(' / ') || '—' },
      { title: 'Remarks', dataIndex: 'remarks', key: 'r', ellipsis: true, render: (s: string | null) => s || '—' },
    ],
    []
  );

  const exportCsv = () => {
    const h = [
      'created_at',
      'movement_type',
      'asset_code',
      'asset_name',
      'from_site',
      'from_building',
      'from_room',
      'to_site',
      'to_building',
      'to_room',
      'remarks',
    ];
    const lines: string[] = [h.join(',')];
    for (const r of rows) {
      lines.push(
        [
          toCsvEsc(r.created_at),
          toCsvEsc(r.movement_type),
          toCsvEsc(r.asset_code),
          toCsvEsc(r.asset_name),
          toCsvEsc(r.from_site),
          toCsvEsc(r.from_building),
          toCsvEsc(r.from_room),
          toCsvEsc(r.to_site),
          toCsvEsc(r.to_building),
          toCsvEsc(r.to_room),
          toCsvEsc(r.remarks),
        ].join(',')
      );
    }
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'asset-movements.csv';
    a.click();
    URL.revokeObjectURL(url);
    message.success('CSV ready');
  };

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Card>Access denied. Need asset.view</Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 8 }}>
      <Space>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset/reports')}>
          All asset reports
        </Button>
        <Link to="/asset/assets">Register</Link>
      </Space>
      <Card
        style={{ marginTop: 8 }}
        title="Movement log"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />} onClick={exportCsv} disabled={rows.length === 0}>
              Export CSV
            </Button>
            <Button onClick={() => void refetch()}>Refresh</Button>
          </Space>
        }
      >
        <Space wrap style={{ marginBottom: 12 }}>
          <Text type="secondary">Date from</Text>
          <DatePicker value={dateFrom} onChange={setDateFrom} allowClear />
          <Text type="secondary">Date to</Text>
          <DatePicker value={dateTo} onChange={setDateTo} allowClear />
          <Text type="secondary">Type</Text>
          <Select
            allowClear
            style={{ width: 140 }}
            placeholder="All"
            value={mType}
            onChange={setMType}
            options={[
              { value: 'issue', label: 'Issue' },
              { value: 'return', label: 'Return' },
              { value: 'transfer', label: 'Transfer' },
            ]}
          />
        </Space>
        <Table<MovementReportRow> rowKey="id" loading={isLoading} dataSource={rows} columns={cols} scroll={{ x: 900 }} />
      </Card>
    </div>
  );
};

export default AssetMovementReportPage;
